export const FREE_PREVIEW_CHECKOUT_SELECTION_KEY = "wheel-visualizer:selected-free-preview";

export function getCheckoutSuccessUrl(
  appUrl: string,
  selectedGenerationId?: string
): string {
  const successUrl = selectedGenerationId
    ? new URL("/app", appUrl)
    : new URL("/credits", appUrl);
  successUrl.searchParams.set("success", "true");
  if (selectedGenerationId) {
    successUrl.searchParams.set("generationId", selectedGenerationId);
  }
  return successUrl.toString();
}

type PreviewStatus = "pending" | "processing" | "completed" | "failed";

export interface FreePreviewGenerationLike {
  id: string;
  vehicleUploadId: string;
  isFreePreview: boolean;
  isUnlocked: boolean;
  status: PreviewStatus;
  resultImageUrl: string | null;
  completedAt: Date | string | null;
  createdAt: Date | string;
}

function generationTimestamp(generation: FreePreviewGenerationLike): number {
  return new Date(generation.completedAt || generation.createdAt).getTime();
}

export function canContinueFromFreePreview(input: {
  isFreePreview: boolean;
  isUnlocked: boolean;
  remainingFreePreviews: number;
}): boolean {
  return input.isFreePreview && !input.isUnlocked && input.remainingFreePreviews > 0;
}

export function getFreePreviewComparisonCandidates<T extends FreePreviewGenerationLike>(input: {
  currentGeneration?: T | null;
  generations: T[];
  preferredVehicleUploadId?: string | null;
}): T[] {
  const uniqueCompletedPreviews = [
    ...(input.currentGeneration ? [input.currentGeneration] : []),
    ...input.generations,
  ]
    .filter(
      (candidate, index, all) =>
        candidate.isFreePreview &&
        candidate.status === "completed" &&
        Boolean(candidate.resultImageUrl) &&
        all.findIndex((item) => item.id === candidate.id) === index
    )
    .sort((a, b) => generationTimestamp(b) - generationTimestamp(a));

  const preferredVehicleId = input.preferredVehicleUploadId;
  const preferredVehicleHasPair = Boolean(preferredVehicleId) &&
    uniqueCompletedPreviews.filter(
      (candidate) => candidate.vehicleUploadId === preferredVehicleId
    ).length >= 2;

  const comparisonVehicleId = preferredVehicleHasPair
    ? preferredVehicleId
    : uniqueCompletedPreviews.find(
        (candidate) =>
          uniqueCompletedPreviews.filter(
            (item) => item.vehicleUploadId === candidate.vehicleUploadId
          ).length >= 2
      )?.vehicleUploadId;

  if (!comparisonVehicleId) return [];

  return uniqueCompletedPreviews
    .filter((candidate) => candidate.vehicleUploadId === comparisonVehicleId)
    .slice(0, 2);
}

export function getComparisonPrimaryAction(creditBalance: number): "unlock" | "checkout" {
  return creditBalance >= 1 ? "unlock" : "checkout";
}

export function findCheckoutRestoreCandidate<T extends FreePreviewGenerationLike>(input: {
  generations: T[];
  selectedGenerationId: string | null;
  creditBalance: number;
}): T | null {
  if (!input.selectedGenerationId || input.creditBalance < 1) return null;

  return input.generations.find(
    (candidate) =>
      candidate.id === input.selectedGenerationId &&
      candidate.isFreePreview &&
      !candidate.isUnlocked &&
      candidate.status === "completed" &&
      Boolean(candidate.resultImageUrl)
  ) || null;
}
